import { createFileRoute, useParams } from '@tanstack/react-router';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from 'react';

import { CtLogo } from '@/components/brand/index.js';
import { getRuntimeConfig } from '@/lib/runtime-config.js';
import {
  fetchSharedDashboard,
  type SharedDashboard,
} from '@/lib/api/dashboard-shares.js';
import { makeSnapshotOnlyToolProvider } from '@/lib/api/dashboard-tool-provider.js';
import { signInWithSso } from '@/lib/sso.js';
import { logger } from '@/logger.js';
import { DashboardRenderer } from '@/ui/dashboards/DashboardRenderer.js';
import {
  isHtmlSnapshot,
  isStaticSnapshot,
} from '@/ui/dashboards/dashboard-snapshot.js';
import { PageContainer } from '@/ui/PageContainer.js';

import { StaticBadge } from '../../features/dashboards/StaticBadge.js';
import { stripLeadingTitle } from '../../features/dashboards/strip-leading-title.js';

/**
 * No `beforeLoad` guard here: `boot.tsx` picks the gate-free public tree once from the first pathname,
 * so a redirect to `/` from this route would land inside the app with no `AuthGate`. A switched-off
 * Dashboards section is handled in place instead (see `SharedDashboardPage`).
 */
export const Route = createFileRoute('/dashboards_/shared/$shareId')({
  component: SharedDashboardPage,
});

type ViewerState =
  | { kind: 'loading' }
  | { kind: 'ready'; dashboard: SharedDashboard }
  | { kind: 'needs-auth' }
  | { kind: 'unavailable' }
  | { kind: 'error'; message: string };

/**
 * Chrome-free public viewer for a shared, read-only dashboard.
 *
 * Deliberately isolated from the authenticated app, same reasoning as
 * `share.$shareId.tsx`: no Teams SDK, no identity bootstrap. The
 * `/dashboards/shared/*` bypass in `boot.tsx` (via `isPublicViewPath`) is what
 * lets this render with no session at all. The snapshot renders through a
 * {@link makeSnapshotOnlyToolProvider}, which answers every `Query()` from the
 * author's last-stored rows and never reaches the network — so a viewer here
 * sees exactly the last refresh and can never trigger one, and there is no
 * Refresh control.
 */
function SharedDashboardPage(): JSX.Element {
  const { shareId } = useParams({ from: '/dashboards_/shared/$shareId' });
  const [state, setState] = useState<ViewerState>({ kind: 'loading' });

  const load = useCallback(async (): Promise<void> => {
    /** An admin who turned the section off closed its links too; nothing is fetched for one. */
    if (!getRuntimeConfig().features.dashboards) {
      setState({ kind: 'unavailable' });
      return;
    }
    setState({ kind: 'loading' });
    try {
      const result = await fetchSharedDashboard(shareId);
      if (result.kind === 'ready') {
        setState({ kind: 'ready', dashboard: result.dashboard });
      } else {
        setState(result);
      }
    } catch (err: unknown) {
      logger.warn('Shared dashboard fetch failed', err);
      setState({
        kind: 'error',
        message: 'Something went wrong loading this dashboard.',
      });
    }
  }, [shareId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (state.kind === 'loading') {
    return (
      <ViewerShell>
        <p className="text-sm text-muted-foreground" role="status">
          Loading dashboard…
        </p>
      </ViewerShell>
    );
  }

  if (state.kind === 'needs-auth') {
    return <SignInScreen onSignedIn={() => void load()} />;
  }

  if (state.kind === 'unavailable') {
    return (
      <ViewerShell>
        <p className="text-sm text-muted-foreground">
          This dashboard is unavailable or has been revoked.
        </p>
      </ViewerShell>
    );
  }

  if (state.kind === 'error') {
    return (
      <ViewerShell>
        <p className="text-sm text-destructive">{state.message}</p>
      </ViewerShell>
    );
  }

  return <ReadOnlyDashboard dashboard={state.dashboard} />;
}

/**
 * Renders the ready state: the snapshot in the same 4xl page container and card the Dashboards
 * page uses, so a link shows the dashboard at the width its author saw. Snapshot-only tool provider.
 */
function ReadOnlyDashboard({
  dashboard,
}: {
  dashboard: SharedDashboard;
}): JSX.Element {
  const storedData = useRef(dashboard.snapshot.data);
  storedData.current = dashboard.snapshot.data;

  const toolProvider = useMemo(
    () => makeSnapshotOnlyToolProvider(storedData),
    [dashboard],
  );

  const renderSnapshot = useMemo(
    () =>
      isHtmlSnapshot(dashboard.snapshot)
        ? dashboard.snapshot
        : {
            ...dashboard.snapshot,
            doc: stripLeadingTitle(dashboard.snapshot.doc, [
              dashboard.name,
              dashboard.snapshot.title ?? '',
            ]),
          },
    [dashboard],
  );

  const fixedNumbers = isStaticSnapshot(dashboard.snapshot);
  const updatedLabel = fixedNumbers
    ? 'Numbers do not refresh'
    : dashboard.lastRefreshedAt === null
      ? 'Not refreshed yet'
      : `Updated ${new Date(dashboard.lastRefreshedAt).toLocaleString()}`;

  return (
    <div
      className="flex h-full min-h-0 w-full flex-col"
      style={{ height: 'var(--app-height, 100dvh)' }}
    >
      <header className="flex shrink-0 items-center gap-3 border-b border-border/40 bg-background px-4 py-3">
        <CtLogo
          variant="mark"
          tone="mono"
          className="size-6 text-[#18399b] [[data-theme='dark']_&]:text-white"
          aria-label="Customertimes"
        />
        <span className="text-sm font-medium text-foreground">
          Read-only dashboard
        </span>
      </header>
      <div className="min-h-0 flex-1 overflow-auto">
        <PageContainer
          maxWidth={isHtmlSnapshot(dashboard.snapshot) ? 'dashboard' : '4xl'}
        >
          <article className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4 shadow-elev-1">
            <header className="flex min-w-0 flex-col gap-0.5">
              <h1 className="truncate text-sm font-semibold text-foreground">
                {dashboard.name}
              </h1>
              <p className="flex items-center gap-1.5 text-caption text-muted-foreground">
                {fixedNumbers && <StaticBadge />}
                {updatedLabel}
              </p>
            </header>
            <DashboardRenderer
              snapshot={renderSnapshot}
              toolProvider={toolProvider}
              fit="natural"
              fallback={
                <p className="text-sm text-muted-foreground">
                  This dashboard cannot be rendered.
                </p>
              }
            />
          </article>
        </PageContainer>
      </div>
    </div>
  );
}

/** Shared full-screen frame for the loading / unavailable / error states. */
function ViewerShell({ children }: { children: JSX.Element }): JSX.Element {
  return (
    <div
      className="flex w-full flex-col items-center justify-center gap-6 bg-background px-6 text-center"
      style={{ height: 'var(--app-height, 100dvh)' }}
    >
      <div className="flex items-center gap-3">
        <CtLogo
          variant="mark"
          tone="mono"
          className="size-9 text-[#18399b] [[data-theme='dark']_&]:text-white"
          aria-label="Customertimes"
        />
        <span className="font-heading text-xl font-bold tracking-tight text-foreground">
          Agent
        </span>
      </div>
      {children}
    </div>
  );
}

/** 401 screen — this dashboard is shared by name, so a Customertimes sign-in is required. */
function SignInScreen({ onSignedIn }: { onSignedIn: () => void }): JSX.Element {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSignIn(): Promise<void> {
    if (loading) return;
    setLoading(true);
    setError(null);
    try {
      await signInWithSso();
      onSignedIn();
    } catch {
      setError('Sign-in failed. Try again.');
      setLoading(false);
    }
  }

  return (
    <ViewerShell>
      <div className="flex flex-col items-center gap-4">
        <p className="max-w-sm text-sm text-muted-foreground">
          This dashboard is shared with specific Customertimes employees.
        </p>
        <button
          type="button"
          onClick={() => void onSignIn()}
          disabled={loading}
          className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-primary px-6 text-sm font-medium text-primary-foreground transition-all hover:shadow-lg active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {loading ? 'Signing in…' : 'Sign in with Microsoft to view'}
        </button>
        {error !== null && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
      </div>
    </ViewerShell>
  );
}
